import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  LayoutDashboard, Clock, Flag, Briefcase, Megaphone, Radar, Music, Truck,
  UserCheck, Users, Mail, Package, Store, Handshake, ArrowRight, Check, X, Sparkles, MapPin, Copy,
} from "lucide-react";

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ACTION_CARDS = [
  { key: "pending_events", label: "Événements à valider", icon: Clock, section: "section-pending" },
  { key: "open_reports", label: "Signalements ouverts", icon: Flag, section: "section-moderation" },
  { key: "new_leads", label: "Leads B2B à traiter", icon: Briefcase, section: "section-partners" },
  { key: "pending_opportunities", label: "Appels à participation en attente", icon: Megaphone, section: "section-opportunities" },
  { key: "new_discoveries", label: "Découvertes à trier", icon: Radar, section: "section-discovery" },
  { key: "pending_concerts", label: "Concerts détectés", icon: Music, section: "section-discovery" },
  { key: "pending_pros", label: "Pros à valider", icon: UserCheck, section: "section-professionals" },
  { key: "duplicate_events", label: "Doublons potentiels", icon: Copy, section: "section-duplicates" },
];

const TOTAL_CARDS = [
  { key: "approved_events", label: "Événements en ligne", icon: Clock },
  { key: "users", label: "Membres", icon: Users },
  { key: "subscribers", label: "Abonnés newsletter", icon: Mail },
  { key: "products", label: "Produits boutique", icon: Package },
  { key: "directory", label: "Fiches annuaire", icon: Store },
  { key: "partners", label: "Partenaires", icon: Handshake },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
};

const RowLink = ({ testid, icon: Icon, title, subtitle, onClick }) => (
  <button data-testid={testid} onClick={onClick}
    className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[#1a1d24] transition-colors group">
    <Icon className="w-4 h-4 text-stone-500 shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="text-sm text-stone-200 truncate">{title}</div>
      {subtitle ? <div className="text-xs text-stone-500 truncate">{subtitle}</div> : null}
    </div>
    <ArrowRight className="w-4 h-4 text-stone-700 group-hover:text-stone-400 transition-colors shrink-0" />
  </button>
);

export const AdminDashboard = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["adminDashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
    refetchInterval: 60000,
  });

  const moderate = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/admin/events/${id}/moderate`, { action })).data,
    onSuccess: (_res, vars) => {
      toast.success(vars.action === "approve" ? "Événement validé" : "Événement rejeté");
      qc.invalidateQueries({ queryKey: ["adminDashboard"] });
      qc.invalidateQueries({ queryKey: ["adminPending"] });
    },
    onError: () => toast.error("Action impossible"),
  });

  const action = data?.action_required || {};
  const totals = data?.totals || {};
  const recent = data?.recent || {};
  const totalToProcess = ACTION_CARDS.reduce((s, c) => s + (action[c.key] || 0), 0);
  const nothingToProcess = !isLoading && totalToProcess === 0;

  return (
    <div className="mb-14" data-testid="admin-dashboard">
      <div className="flex items-center gap-2 mb-5">
        <LayoutDashboard className="w-5 h-5 text-amber-500" />
        <h2 className="font-serif text-2xl text-stone-100">Tableau de bord</h2>
        {data && totalToProcess > 0 && (
          <span data-testid="dashboard-total-badge" className="ml-1 inline-flex items-center rounded-full bg-amber-500/15 border border-amber-600/40 px-2.5 py-0.5 font-mono text-[11px] text-amber-300">
            {totalToProcess} à traiter
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {ACTION_CARDS.map((c) => {
          const n = action[c.key] ?? 0;
          const active = n > 0;
          const Icon = c.icon;
          return (
            <button key={c.key} data-testid={`dash-action-${c.key}`} onClick={() => scrollToSection(c.section)}
              className={`group text-left rounded-lg p-5 border transition-colors duration-200 ${
                active ? "bg-amber-950/40 border-amber-700/60 hover:border-amber-500" : "bg-[#121418] border-[#272A34] hover:border-[#3a3f4d]"
              }`}>
              <div className="flex items-center justify-between mb-2.5">
                <Icon className={`w-5 h-5 ${active ? "text-amber-400" : "text-stone-500"}`} />
                {active ? <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  : <ArrowRight className="w-4 h-4 text-stone-700 opacity-0 group-hover:opacity-100 transition-opacity" />}
              </div>
              <div className={`font-serif text-3xl leading-none ${active ? "text-amber-300" : "text-stone-500"}`}>{isLoading ? "—" : n}</div>
              <div className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-stone-500">{c.label}</div>
            </button>
          );
        })}
      </div>

      {/* Preview: act without scrolling */}
      <div className="mt-5 bg-[#0F1115] border border-[#1e2129] rounded-xl p-5" data-testid="dashboard-preview">
        <div className="font-mono text-[11px] uppercase tracking-wider text-stone-500 mb-3">Derniers éléments à traiter</div>

        {nothingToProcess ? (
          <div className="flex items-center gap-2 text-stone-500 py-6 justify-center" data-testid="dashboard-empty">
            <Sparkles className="w-4 h-4 text-amber-500/70" />
            <span className="text-sm">Rien à traiter — tout est à jour.</span>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Pending events with inline actions */}
            {(recent.events || []).length > 0 && (
              <div data-testid="preview-events">
                <div className="flex items-center gap-1.5 text-xs text-amber-400/90 mb-1.5"><Clock className="w-3.5 h-3.5" /> Événements à valider</div>
                <div className="divide-y divide-[#1a1d24]">
                  {recent.events.map((e) => (
                    <div key={e.id} data-testid={`preview-event-${e.id}`} className="flex items-center gap-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-stone-200 truncate">{e.title}</div>
                        <div className="text-xs text-stone-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{[e.city, fmtDate(e.start_date)].filter(Boolean).join(" · ")}</div>
                      </div>
                      <button data-testid={`preview-approve-${e.id}`} disabled={moderate.isPending}
                        onClick={() => moderate.mutate({ id: e.id, action: "approve" })}
                        className="p-1.5 rounded-md bg-emerald-900/40 border border-emerald-700/50 text-emerald-300 hover:bg-emerald-800/50 disabled:opacity-40 transition-colors" title="Valider">
                        <Check className="w-4 h-4" />
                      </button>
                      <button data-testid={`preview-reject-${e.id}`} disabled={moderate.isPending}
                        onClick={() => moderate.mutate({ id: e.id, action: "reject" })}
                        className="p-1.5 rounded-md bg-red-950/40 border border-red-800/50 text-red-300 hover:bg-red-900/50 disabled:opacity-40 transition-colors" title="Rejeter">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(recent.reports || []).length > 0 && (
              <div data-testid="preview-reports">
                <div className="flex items-center gap-1.5 text-xs text-amber-400/90 mb-1.5"><Flag className="w-3.5 h-3.5" /> Signalements</div>
                {recent.reports.map((rp) => (
                  <RowLink key={rp.id} testid={`preview-report-${rp.id}`} icon={Flag}
                    title={rp.target_name || rp.target_type || "Signalement"} subtitle={rp.reason}
                    onClick={() => scrollToSection("section-moderation")} />
                ))}
              </div>
            )}

            {(recent.leads || []).length > 0 && (
              <div data-testid="preview-leads">
                <div className="flex items-center gap-1.5 text-xs text-amber-400/90 mb-1.5"><Briefcase className="w-3.5 h-3.5" /> Leads B2B</div>
                {recent.leads.map((l) => (
                  <RowLink key={l.id} testid={`preview-lead-${l.id}`} icon={Briefcase}
                    title={l.company || l.name || l.email} subtitle={[l.name, fmtDate(l.created_at)].filter(Boolean).join(" · ")}
                    onClick={() => scrollToSection("section-partners")} />
                ))}
              </div>
            )}

            {(recent.discoveries || []).length > 0 && (
              <div data-testid="preview-discoveries">
                <div className="flex items-center gap-1.5 text-xs text-amber-400/90 mb-1.5"><Radar className="w-3.5 h-3.5" /> Découvertes</div>
                {recent.discoveries.map((d) => (
                  <RowLink key={d.id} testid={`preview-discovery-${d.id}`} icon={Radar}
                    title={d.title} subtitle={[d.city, d.source_label].filter(Boolean).join(" · ")}
                    onClick={() => scrollToSection("section-discovery")} />
                ))}
              </div>
            )}

            {(recent.opportunities || []).length > 0 && (
              <div data-testid="preview-opportunities">
                <div className="flex items-center gap-1.5 text-xs text-amber-400/90 mb-1.5"><Megaphone className="w-3.5 h-3.5" /> Appels à participation</div>
                {recent.opportunities.map((o) => (
                  <RowLink key={o.id} testid={`preview-opportunity-${o.id}`} icon={Megaphone}
                    title={o.title} subtitle={fmtDate(o.created_at)}
                    onClick={() => scrollToSection("section-opportunities")} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-4">
        {TOTAL_CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} data-testid={`dash-total-${c.key}`} className="bg-[#0F1115] border border-[#1e2129] rounded-lg p-4">
              <Icon className="w-4 h-4 text-stone-600 mb-1.5" />
              <div className="font-serif text-xl text-stone-300">{isLoading ? "—" : totals[c.key] ?? 0}</div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-stone-600 mt-0.5">{c.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminDashboard;
